import {
    DEPARTMENTS_LOADED,
    DEPARTMENTS_FILTER_CHANGED,
    ASSIGNED_DEPARTMENTS_LOADED
} from '../store/actionTypes';
import {createSelector} from 'reselect';

import * as _ from 'lodash';

import {
    AssignedDepartmentManagers,
    Department,
    DepartmentApprover,
    DepartmentChanges,
    DepartmentManagerUser,
    DepartmentsAction,
    DepartmentsLoadedData,
    DepartmentsState
} from "types/departments";


export const DEFAULT_STATE: DepartmentsState = {
    possibleManagers: [],
    assignedManagers: [],
    allDepartments: [],
    filter: ""
};

export const getDepartmentsForRender = createSelector(
    [getDepartments, getAssignedManagers, getDepartmentsFilter],
    (departments: Department[], managers, filter: string) => {
        const matching = _.filter(departments, (department) => {
            const attributesList: (keyof Department)[] = ["name", "organizationUnitId"];

            // fulltext search over all attributes in attributesList
            for (const attribute of attributesList) {
                const value = department[attribute];
                if (value && value.toString().toLowerCase().indexOf(filter) !== -1)
                    return true;
            }

            //filter by managers
            const isManager = _.find(department.managers, manager => {
                return manager.label.indexOf(filter) !== -1;
            });

            return !!isManager;
        });

        return _.sortBy(matching, (department) => department.name);
    }
);

/**
 * the picker works with the name and email of an approver, the API and the save only with the id,
 * and an id that is no longer a user of the company has to keep its place in the list
 */
function toApproverOptions(managerIds: (number | null)[], possibleManagers: DepartmentManagerUser[]): DepartmentApprover[] {
    return managerIds.map(id => {
        let user = _.find(possibleManagers, manager => {
            return manager.idUser === id
        });

        if (user) {
            return {
                value: user.idUser,
                label: user.firstName + " " + user.lastName + "(" + user.email + ")"
            };
        }
        else {
            return {value: null, label: ""};
        }
    });
}

export default function departmentsReducer(state: DepartmentsState = DEFAULT_STATE, action: DepartmentsAction): DepartmentsState {
    let partialState: Partial<DepartmentsState>;
    switch (action.type) {
        case DEPARTMENTS_LOADED: {
            const data = action.data as DepartmentsLoadedData;
            partialState = {
                possibleManagers: data.possibleManagers,
                assignedManagers: data.assignedManagers
            };

            let mappedData: Department[] = [];
            data.departments.forEach(department => {
                let foundOrgUnit = _.find(Object.keys(data.assignedManagers), (man => {
                        return man === department.internalOrgUnitId.toString();
                    }
                ));

                if (foundOrgUnit) {
                    department.managers = toApproverOptions(data.assignedManagers[foundOrgUnit], data.possibleManagers);
                }
                mappedData.push(department);
            });

            partialState.allDepartments = mappedData;
            return Object.assign({}, state, partialState);
        }

        case DEPARTMENTS_FILTER_CHANGED:
            partialState = {filter: action.data as string};

            return Object.assign({}, state, partialState);

        case ASSIGNED_DEPARTMENTS_LOADED: {
            partialState = Object.assign({}, state);

            const {departmentId, departmentManagerIds} = action.data as DepartmentChanges;

            let changedDepartmentIndex = _.findIndex(partialState.allDepartments, department => {
                return department.internalOrgUnitId === departmentId;
            });

            if (changedDepartmentIndex !== -1) {
                partialState.allDepartments[changedDepartmentIndex].managers =
                    toApproverOptions(departmentManagerIds, partialState.possibleManagers);
            }

            return Object.assign({}, state, partialState);
        }

        default:
            break;
    }

    return state;
}

export function getFormatedPossibleManagers(state: DepartmentsState): DepartmentApprover[] {
    if (!state.possibleManagers || state.possibleManagers.length === 0)
        return [];

    let response = state.possibleManagers.map(user => {
        return {
            value: user.idUser,
            label: user.firstName + " " + user.lastName + "(" + user.email + ")"
        }

    });
    return response;
}

export function getAssignedManagers(state: DepartmentsState): AssignedDepartmentManagers | number[] {
    return state.assignedManagers;
}

export function getDepartments(state: DepartmentsState): Department[] {
    return state.allDepartments;
}

export function getDepartmentsFilter(state: DepartmentsState): string {
    return state.filter;
}
